import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { DashboardLayout } from '@/components/layout'
import { PageHeader } from '@/lib/atsUi'
import { atsNav } from '@/lib/atsNav'
import { currentTenant, currentUser, consentRecords, dataRequests } from '@/lib/mocks'
import { DataList } from '@/components/ui/data-list'
import { Tabs } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Download, Plus } from 'lucide-react'
import { toast } from 'sonner'

export const Route = createFileRoute('/compliance')({
  component: CompliancePage,
})

function CompliancePage() {
  const [tab, setTab] = useState<'consent' | 'requests'>('consent')
  return (
    <DashboardLayout title="Compliance" navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <PageHeader
        title="Compliance"
        subtitle="Consent and data-rights — DPDP-ready from day one."
      />

      <Tabs
        items={[
          { value: 'consent', label: 'Consent records', count: consentRecords.length },
          { value: 'requests', label: 'Data requests', count: dataRequests.length },
        ]}
        value={tab}
        onChange={(v) => setTab(v as 'consent' | 'requests')}
      />

      {/* Consent records */}
      {tab === 'consent' && (
      <div className="space-y-3 mb-8">
        <DataList
          items={consentRecords}
          getKey={(record) => record.id}
          pageSize={5}
          empty="No consent records yet."
          renderRow={(record) => (
            <div className="flex flex-col gap-2 px-5 py-4 sm:flex-row sm:items-center sm:gap-4 transition-colors hover:bg-muted/40">
              {/* Candidate */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium leading-snug">{record.candidate}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{record.purpose}</p>
              </div>

              {/* Basis */}
              <div className="shrink-0">
                {record.basis === 'voluntary' ? (
                  <Badge variant="success">Voluntary</Badge>
                ) : (
                  <Badge variant="warning">Cold source</Badge>
                )}
              </div>

              {/* Status */}
              <div className="shrink-0">
                {record.status === 'granted' && <Badge variant="success">Granted</Badge>}
                {record.status === 'withdrawn' && <Badge variant="info">Withdrawn</Badge>}
                {record.status === 'pending' && <Badge variant="warning">Pending</Badge>}
              </div>

              {/* Date */}
              <div className="text-sm text-muted-foreground sm:w-28 shrink-0 text-right">
                {record.date}
              </div>
            </div>
          )}
        />
      </div>
      )}

      {/* Data-subject requests */}
      {tab === 'requests' && (
      <div className="space-y-3">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-muted-foreground">Data-subject requests</h2>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => toast.success('Data exported')}
            >
              <Download className="mr-2 h-4 w-4" />
              Export data
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => toast.success('Request logged')}
            >
              <Plus className="mr-2 h-4 w-4" />
              Log request
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground -mt-2 mb-3">
          Action within your published SLA.
        </p>
        <DataList
          items={dataRequests}
          getKey={(req) => req.id}
          pageSize={5}
          empty="No data-subject requests yet."
          renderRow={(req) => (
            <div className="flex flex-col gap-2 px-5 py-4 sm:flex-row sm:items-center sm:gap-4 transition-colors hover:bg-muted/40">
              {/* Candidate */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium leading-snug">{req.candidate}</p>
              </div>

              {/* Type */}
              <div className="shrink-0">
                <Badge variant="info">{req.type}</Badge>
              </div>

              {/* Status */}
              <div className="shrink-0">
                {req.status === 'open' ? (
                  <Badge variant="warning">Open</Badge>
                ) : (
                  <Badge variant="success">Completed</Badge>
                )}
              </div>

              {/* Due */}
              <div className="text-sm text-muted-foreground sm:w-28 shrink-0 text-right">
                {req.due}
              </div>
            </div>
          )}
        />
      </div>
      )}
    </DashboardLayout>
  )
}
